import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader2, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import { formatDisplayDateTime } from '@ooh/ui/lib/date-format';

export interface AuditLogEntry {
  id: number;
  /** ISO timestamp. */
  timestamp: string;
  actor: string;
  roleAffected: string;
  description: string;
}

export interface AuditHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  /** The panel body: `AuditEntryList` once data is in, `AuditHistoryLoading` while it loads. */
  children: ReactNode;
}

export function AuditHistoryLoading() {
  const { t } = useTranslation('admin');
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span className="text-[13px]">{t('permissionMatrix.auditPanel.loading')}</span>
    </div>
  );
}

export function AuditEntryList({ entries }: { entries: AuditLogEntry[] }) {
  const { t } = useTranslation('admin');
  if (entries.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground text-center py-10">
        {t('permissionMatrix.auditPanel.emptyState')}
      </p>
    );
  }
  return (
    <>
      {entries.map((entry) => (
        <div key={entry.id} className="border-b border-border/30 pb-4 last:border-b-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-foreground">{entry.actor}</span>
            <span className="text-2xs text-muted-foreground">
              {formatDisplayDateTime(entry.timestamp)}
            </span>
          </div>
          <Badge variant="outline" className="text-2xs mb-1.5">
            {entry.roleAffected}
          </Badge>
          <p className="text-[13px] text-foreground/90 leading-snug">{entry.description}</p>
        </div>
      ))}
    </>
  );
}

/** Slide-over shell listing recent role/permission changes; the caller supplies the body. */
export function AuditHistoryPanel({ open, onClose, children }: AuditHistoryPanelProps) {
  const { t } = useTranslation('admin');

  return (
    <>
      <div
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-background border-l border-border shadow-xl flex flex-col transition-transform duration-200 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
        data-testid="audit-history-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-[15px] font-semibold">{t('permissionMatrix.auditPanel.title')}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('permissionMatrix.auditPanel.closeAriaLabel')}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">{children}</div>
      </aside>
    </>
  );
}
